/**
 * The bus: how one screen asks another for something (open a draft, show a
 * subject, run a prompt, play a use case, name a World, open the palette).
 *
 * Every hand-off is typed by `AppEvents` in contracts.ts, so a sender and a
 * listener cannot disagree on a name or a detail the way document events
 * could. The newest handler of a kind is asked first (the screen mounted
 * last is the one in front); one that returns `false` declines and the next
 * is asked. `emit` says whether anyone took it, so a sender can fall back.
 * With `keep`, a hand-off no one took waits, the latest per kind, for the
 * next handler of that kind: the Gallery plays a use case asked for while
 * the route was still on its way there.
 */

import type { AppBus, AppEvents, AppHandler, CreateBus, Unsub } from '../contracts';

type Kind = keyof AppEvents;
type AnyHandler = (detail: unknown) => boolean | void;

export const createBus: CreateBus = () => {
  const handlers = new Map<Kind, AnyHandler[]>();
  const kept = new Map<Kind, unknown>();

  /** True when `fn` took it: anything but `false`. A throwing handler has not taken it. */
  const offer = (fn: AnyHandler, detail: unknown): boolean => {
    try {
      return fn(detail) !== false;
    } catch {
      return false;
    }
  };

  const bus: AppBus = {
    emit(kind, detail, opts) {
      const list = [...(handlers.get(kind) ?? [])].reverse();
      for (const fn of list) {
        if (offer(fn, detail)) {
          kept.delete(kind);
          return true;
        }
      }
      if (opts?.keep) kept.set(kind, detail);
      return false;
    },
    on<K extends Kind>(kind: K, fn: AppHandler<K>): Unsub {
      const h = fn as AnyHandler;
      handlers.set(kind, [...(handlers.get(kind) ?? []), h]);
      if (kept.has(kind)) {
        const waiting = kept.get(kind);
        kept.delete(kind);
        if (!offer(h, waiting)) kept.set(kind, waiting);
      }
      return () => {
        const list = handlers.get(kind);
        if (!list) return;
        const i = list.lastIndexOf(h);
        if (i >= 0) handlers.set(kind, [...list.slice(0, i), ...list.slice(i + 1)]);
      };
    },
  };
  return bus;
};
